import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useThreads } from '@/hooks/ai-agents/hooks/useThreads';
import CreateThreadButton from './CreateThreadButton';
import ThreadItem from './ThreadItem';
import ThreadsListSkeleton from './ThreadsListSkeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { logInfo, LogCategory } from '@/utils/logger';
import GradientAnimationStyles from './GradientAnimationStyles';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

interface ThreadsListDesktopProps {
  onThreadSelect: (targetThreadId?: string) => Promise<void> | void;
}

const ThreadsListDesktop: React.FC<ThreadsListDesktopProps> = ({ onThreadSelect }) => {
  const { threads, isLoading, deleteThread, isDeleting } = useThreads();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  const handleCreateThread = async () => {
    try {
      setIsCreatingThread(true);
      
      logInfo('Starting new conversation via unified switchToChatTab', { 
        category: LogCategory.USER_ACTION
      });
      
      // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
      if (usageData.remainingBalance === 0) {
        console.log('[CreateAgentConversation] Insufficient credits - blocking thread creation and showing modal');
        setIsCreatingThread(false);
        openOutOfCreditsModal();
        return; // Clean early exit - no cascading errors
      }

      console.log(`[CreateAgentConversation] Credits available (${usageData.remainingBalance}) - proceeding with unified thread creation`);
      
      // ✅ UNIFIED: Use onThreadSelect without targetThreadId for new thread creation
      // This handles both thread creation AND appropriate navigation for context
      await onThreadSelect();
      
      // Focus the input after a short delay to ensure UI has updated
      setTimeout(() => {
        const messageInput = document.querySelector('[data-copilot-input="true"]') as HTMLTextAreaElement;
        if (messageInput) {
          messageInput.focus();
        }
      }, 300);
      
    } catch (error) {
      // Error handling: switchToChatTab handles all error cases internally
      console.error('Failed to start new conversation via unified function:', error);
      logInfo('Thread creation failed via unified function', { 
        category: LogCategory.USER_ACTION,
        data: { error: error.message }
      });
    } finally {
      setIsCreatingThread(false);
    }
  };

  const handleThreadSelect = async (threadId: string) => {
    try {
      logInfo('Thread selected via unified switchToChatTab', { 
        category: LogCategory.USER_ACTION,
        data: { threadId }
      });
      
      // ✅ UNIFIED: Use onThreadSelect with specific threadId
      // This handles both thread loading AND appropriate navigation for context
      await onThreadSelect(threadId);
      
      // Focus the input after navigation
      setTimeout(() => {
        const messageInput = document.querySelector('[data-copilot-input="true"]') as HTMLTextAreaElement;
        if (messageInput) {
          messageInput.focus();
        }
      }, 100);
      
    } catch (error) {
      // Error handling: switchToChatTab handles all error cases internally
      console.error('Failed to load thread via unified function:', error);
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    setSelectedThreadId(threadId);
    setShowConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedThreadId) {
        await deleteThread(selectedThreadId);
        setSelectedThreadId(null);
        setShowConfirmation(false);
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  const cancelDelete = () => {
    setSelectedThreadId(null);
    setShowConfirmation(false);
  };

  // Get the selected thread for better confirmation message
  const selectedThread = selectedThreadId ? threads.find(thread => thread.id === selectedThreadId) : null;
  const threadDisplayName = selectedThread?.lastMsgFromUser || selectedThread?.title || 'this conversation';

  if (isLoading) {
    return <ThreadsListSkeleton itemCount={3} />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Create Thread Button - Optimized for sidebar context */}
      <div className="flex-none mb-3">
        <CreateThreadButton 
          onClick={handleCreateThread}
          isLoading={isCreatingThread}
        />
      </div>
      
      {/* Scrollable Content - Optimized for sidebar context */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full w-full" orientation="vertical">
                      <div className="h-full pb-2 px-1">
            {threads.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center justify-center h-full min-h-[300px] px-6 py-12"
              >
                {/* Illustration */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="relative mb-8"
                >
                  {/* Animated background circles */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="w-24 h-24 rounded-full border-2 border-gradient-to-r from-blue-200/30 to-purple-200/30 dark:from-blue-800/30 dark:to-purple-800/30"
                    />
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      className="absolute w-16 h-16 rounded-full border border-dashed border-blue-300/40 dark:border-blue-700/40"
                    />
                  </div>
                  
                  {/* Chat bubble icon */}
                  <div className="relative z-10 w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <motion.div
                      animate={{ y: [0, -2, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <svg 
                        className="w-10 h-10 text-white" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                        />
                      </svg>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Text content */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="text-center mb-8 space-y-3"
                >
                  <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
                    Start Your First Conversation
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed">
                    Choose an AI assistant and begin chatting to get help with your tasks, generate content, or explore ideas.
                  </p>
                </motion.div>

                {/* Big CTA Button */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCreateThread}
                  className="relative group overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {/* Background animation */}
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  
                  {/* Button content */}
                  <div className="relative flex items-center justify-center">
                    <span>Start New Conversation</span>
                  </div>
                </motion.button>

                {/* Subtle helper text */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="text-xs text-slate-400 dark:text-slate-500 mt-6"
                >
                  Your conversations will appear here
                </motion.p>
              </motion.div>
            ) : (
              <div className="space-y-2">
                <AnimatePresence mode="popLayout" initial={false}>
                  {threads.map((thread, index) => (
                    <motion.div
                      key={thread.id}
                      initial={false}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                      transition={{ 
                        duration: 0.3,
                        ease: "easeOut",
                        layout: { duration: 0.4, ease: "easeInOut" }
                      }}
                      layout
                      layoutId={thread.id}
                    >
                      <ThreadItem
                        thread={thread}
                        onClick={() => handleThreadSelect(thread.id)}
                        onDelete={() => handleDeleteThread(thread.id)}
                        isDeleting={isDeleting}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
      
      {/* Gradient Animation Styles */}
      <GradientAnimationStyles />

      {showConfirmation && (
        <ConfirmDialog
          isOpen={showConfirmation}
          title="Delete Conversation"
          description={`Are you sure you want to delete this conversation? This action cannot be undone and all messages will be permanently lost.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="red"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          isConfirmLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default ThreadsListDesktop; 